import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Spin, theme } from 'antd';
import { IconShieldExclamation } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getMyHostDeposit } from '../services/HostDepositService';
import { IHostDeposit } from '../interfaces/heavyDutyMachinery/IHostDeposit';
import { formatCurrency } from '../helpers/currencyHelper';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL, CORAL_SOFT_BG } from '../theme/creamyTokens';

const { Text, Title } = Typography;

interface HostDepositShortfallSheetProps {
  /** The amount the host was trying to accept -- what the shortfall is worked out against. */
  bookingAmount: number;
  open: boolean;
  onClose: () => void;
}

/**
 * "You cannot accept this booking" is a dead end. This is the same refusal
 * with the way out attached.
 *
 * It appears at the one moment a host is most willing to act on it: they have
 * a customer waiting, a price agreed, and a button that just refused them.
 * Telling them the exact figure to add -- and putting them one tap from
 * adding it -- is the difference between a job this marketplace keeps and one
 * that turns into a phone call and a cash deal.
 *
 * The numbers are worked out from the host's live deposit rather than parsed
 * out of the error, using the same formula the server gates on, so they
 * cannot drift from what accepting will actually require.
 */
const HostDepositShortfallSheet: React.FC<HostDepositShortfallSheetProps> = ({ bookingAmount, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [deposit, setDeposit] = useState<IHostDeposit | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getMyHostDeposit()
      .then((loaded) => {
        if (!cancelled) setDeposit(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open]);

  const money = (value: number) => formatCurrency(value, deposit?.currencyCode ?? 'EUR', i18n.language);

  // Mirrors HostExposureCalculator: the fee on this booking on top of what
  // the host's live bookings already hold up.
  const fee = deposit
    ? Math.round(bookingAmount * (deposit.processingFeePercent / 100) * 100) / 100 + deposit.processingFeeFixed
    : 0;
  const required = deposit ? deposit.currentExposure + fee : 0;
  const shortfall = deposit ? Math.max(0, required - deposit.balance) : 0;

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <span
          style={{
            display: 'flex',
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            background: CORAL_SOFT_BG,
            color: CORAL,
          }}
        >
          <IconShieldExclamation size={20} />
        </span>
        <Title level={5} style={{ margin: 0 }}>
          {translate(t, TRANSLATIONS.HOST_DEPOSIT_SHORTFALL_TITLE)}
        </Title>
      </div>

      {!deposit ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Text style={{ fontSize: 14 }}>
            {t(TRANSLATIONS.HOST_DEPOSIT_SHORTFALL_BODY, {
              required: money(required),
              shortfall: money(shortfall),
            })}
          </Text>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button
              block
              onClick={onClose}
              style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
            >
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
            </Button>
            <Button
              block
              type="primary"
              onClick={() => navigate('/heavy-duty-machinery/host/deposit')}
              style={{ borderRadius: 999 }}
            >
              {translate(t, TRANSLATIONS.HOST_DEPOSIT_SHORTFALL_ACTION)}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default HostDepositShortfallSheet;
